import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { trackSignup } from '../services/analytics';

const Register = () => {
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get('plan') || 'free';
  const alreadyPaid = searchParams.get('paid') === 'true';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [selectedPlan] = useState(initialPlan);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();

  // Check if this is a free plan registration
  const isFreeRegistration = selectedPlan === 'free';

  useEffect(() => {
    // Redirect if already logged in
    if (user) {
      // If user already paid via Pesapal, go directly to dashboard
      if (alreadyPaid) {
        navigate('/dashboard');
        return;
      }
      // If user registered with a paid plan, redirect to pricing page to complete payment
      if (selectedPlan && selectedPlan !== 'free') {
        navigate(`/pricing?plan=${selectedPlan}`);
      } else {
        // Free plan - go directly to dashboard
        navigate('/dashboard');
      }
    }
  }, [user, navigate, selectedPlan, alreadyPaid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // All users register as 'student' role by default
    const result = await register(email, password, fullName, 'student');
    
    if (result.success) {
      // Track signup - determine role based on selected plan
      const role = selectedPlan === 'teacher' ? 'teacher' : 'student';
      trackSignup(role, 'register_page');
      // useEffect will handle redirect
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 transition-colors duration-300">
      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Toggle dark mode"
      >
        {isDarkMode ? (
          <svg className="w-6 h-6 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
          </svg>
        )}
      </button>

      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 transition-colors duration-300">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden bg-[#b8e1f5]">
            <img src="/logo.png" alt="Study Quiz Generator" className="w-16 h-16 object-cover" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isFreeRegistration ? 'Create Free Account' : 'Create Your Account'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm">
            {isFreeRegistration 
              ? 'Get started with 3 free quizzes per day'
              : 'Start generating quizzes in seconds'
            }
          </p>
        </div>

        {/* Free Plan Info Banner */}
        {isFreeRegistration && (
          <div className="mb-6 p-4 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="flex items-center">
              <span className="text-2xl mr-3">✨</span>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Free Plan</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">3 quizzes/day • Text input • Up to 7 questions</p>
              </div>
            </div>
          </div>
        )}

        {/* Paid Plan Info */}
        {!isFreeRegistration && (
          <div className={`mb-6 p-4 rounded-lg border ${
            selectedPlan === 'student' ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700' :
            selectedPlan === 'teacher' ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700' :
            'bg-purple-50 dark:bg-purple-900/30 border-purple-200 dark:border-purple-700'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">
                  {selectedPlan === 'student' ? '🎓' : selectedPlan === 'teacher' ? '👩‍🏫' : '🏫'}
                </span>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedPlan === 'student' ? 'Student Plan' : selectedPlan === 'teacher' ? 'Teacher Pro Plan' : 'Group Plan'}
                    {alreadyPaid && <span className="ml-2 text-green-600 dark:text-green-400">✓ Paid</span>}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {alreadyPaid 
                      ? 'Create your account to access premium features'
                      : 'Unlimited quizzes • All features'
                    }
                  </p>
                </div>
              </div>
              {!alreadyPaid && (
                <Link 
                  to="/pricing" 
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Change plan
                </Link>
              )}
            </div>
            {!alreadyPaid && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                You'll be redirected to complete payment after registration
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                placeholder="••••••••"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Creating account...' : 
              isFreeRegistration ? 'Create Free Account' : 
              alreadyPaid ? 'Create Account & Access Premium' :
              'Create Account & Continue to Payment'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/pricing" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
            Compare all plans →
          </Link>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
